'use client'

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import axios from 'axios'
import { useAuthContext } from './auth-context'
import { subscriptionApi } from '@/lib/api/subscription.api'
import { SubscriptionData } from '@/lib/types/subscription.types'
import { toast } from 'sonner'
import { SubscriptionLimitModal } from '@/components/subscription/subscription-limit-modal'
import { HppCheckoutModal } from '@/components/subscription/hpp-checkout-modal'

/** Public/auth routes must not hit authenticated subscription APIs. */
function isPublicAuthPath(pathname: string | null): boolean {
    if (!pathname) return false
    return (
        pathname.startsWith('/login') ||
        pathname.startsWith('/signup') ||
        pathname.startsWith('/register') ||
        pathname.startsWith('/forgot-password') ||
        pathname.startsWith('/reset-password') ||
        pathname.startsWith('/email-verification')
    )
}

/**
 * handleUpgrade accepts either:
 * handleUpgrade accepts plan_type ('MONTHLY' | 'YEARLY') for MyPaymentNow checkout.
 */
type UpgradeArg = 'MONTHLY' | 'YEARLY' | string

const PENDING_PLAN_STORAGE_KEY = 'pending_subscription_plan_type'

function readPendingPlanType(): 'MONTHLY' | 'YEARLY' | null {
    if (typeof window === 'undefined') return null
    const value = sessionStorage.getItem(PENDING_PLAN_STORAGE_KEY)
    return value === 'MONTHLY' || value === 'YEARLY' ? value : null
}

function writePendingPlanType(planType: 'MONTHLY' | 'YEARLY' | null) {
    if (typeof window === 'undefined') return
    if (planType) {
        sessionStorage.setItem(PENDING_PLAN_STORAGE_KEY, planType)
    } else {
        sessionStorage.removeItem(PENDING_PLAN_STORAGE_KEY)
    }
}

interface SubscriptionContextType {
    subscription: SubscriptionData | null
    isLoading: boolean
    isProfessional: boolean
    isStarter: boolean
    refreshSubscription: () => Promise<void>
    handleUpgrade: (arg: UpgradeArg) => Promise<void>
    cancelSubscription: () => Promise<void>
    /** Manual Sync button: pulls from MyPaymentNow gateway. */
    syncSubscription: () => Promise<void>
    /** After HPP payment: poll webhook, then auto gateway sync. */
    confirmPaymentActivation: () => Promise<boolean>
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined)

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
    const router = useRouter()
    const pathname = usePathname()
    const { isAuthenticated, token, isLoading: authLoading, clearAuth } = useAuthContext()
    const isFetchingRef = useRef(false)
    const [subscription, setSubscription] = useState<SubscriptionData | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null)
    const [isCheckoutOpen, setIsCheckoutOpen] = useState(false)
    const onPublicAuthRoute = isPublicAuthPath(pathname)

    const fetchSubscription = useCallback(async (options?: { fromGateway?: boolean; silent?: boolean }) => {
        // Wait for auth hydrate; never call on login/signup with a stale token.
        if (authLoading || onPublicAuthRoute || !isAuthenticated || !token) {
            if (!authLoading) {
                setSubscription(null)
                setIsLoading(false)
            }
            return
        }

        if (isFetchingRef.current) return
        isFetchingRef.current = true

        if (!options?.silent) {
            setIsLoading(true)
        }
        try {
            const result = options?.fromGateway
                ? await subscriptionApi.refreshFromGateway()
                : await subscriptionApi.getCurrentSubscription()
            if (result.success) {
                setSubscription(result.data)
            } else {
                console.warn('Failed to fetch subscription status')
            }
        } catch (error) {
            // Expired/invalid token → clear session quietly (avoid Next.js Console AxiosError overlay)
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                clearAuth()
                setSubscription(null)
            } else {
                console.warn(
                    'Error fetching subscription:',
                    axios.isAxiosError(error)
                        ? error.response?.status ?? error.message
                        : error
                )
            }
        } finally {
            if (!options?.silent) {
                setIsLoading(false)
            }
            isFetchingRef.current = false
        }
    }, [authLoading, onPublicAuthRoute, isAuthenticated, token, clearAuth])

    // Local plan on auth — keep navigation free of gateway latency
    useEffect(() => {
        if (authLoading) return

        if (onPublicAuthRoute || !isAuthenticated || !token) {
            setSubscription(null)
            setIsLoading(false)
            return
        }

        void fetchSubscription()
    }, [authLoading, onPublicAuthRoute, isAuthenticated, token, fetchSubscription])

    // Background MyPaymentNow sync after idle — must not compete with route changes
    useEffect(() => {
        if (authLoading || onPublicAuthRoute || !isAuthenticated || !token) return

        let cancelled = false
        const run = () => {
            if (!cancelled) {
                void fetchSubscription({ fromGateway: true, silent: true })
            }
        }

        const idle = window as Window & {
            requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
            cancelIdleCallback?: (id: number) => void
        }

        let idleId: number | undefined
        let timeoutId: ReturnType<typeof setTimeout> | undefined

        if (typeof idle.requestIdleCallback === 'function') {
            idleId = idle.requestIdleCallback(run, { timeout: 8000 })
        } else {
            timeoutId = setTimeout(run, 4000)
        }

        return () => {
            cancelled = true
            if (idleId !== undefined) {
                idle.cancelIdleCallback?.(idleId)
            }
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId)
            }
        }
    }, [authLoading, onPublicAuthRoute, isAuthenticated, token, fetchSubscription])

    const refreshSubscription = useCallback(async () => {
        await fetchSubscription()
    }, [fetchSubscription])

    const handleUpgrade = useCallback(async (arg: UpgradeArg) => {
        // Normalise plan_type from callers that may pass a legacy price slug.
        const upper = (arg || '').toUpperCase()
        const isPlanType = upper === 'MONTHLY' || upper === 'YEARLY'
        const plan_type: 'MONTHLY' | 'YEARLY' | undefined = isPlanType
            ? (upper as 'MONTHLY' | 'YEARLY')
            : arg?.toLowerCase().includes('yearly') || arg?.toLowerCase().includes('annual')
                ? 'YEARLY'
                : arg?.toLowerCase().includes('monthly')
                    ? 'MONTHLY'
                    : undefined
        const price_id = isPlanType ? undefined : arg

        // Safety checks (backend also validates these)
        if (subscription?.plan_type === 'YEARLY') {
            toast.error('You are already on the Yearly plan. Downgrades are currently disabled.')
            return
        }
        if (subscription?.plan_type === 'MONTHLY' && plan_type === 'MONTHLY') {
            toast.info('You are already on the Monthly plan.')
            return
        }

        try {
            toast.loading('Opening secure payment...', { id: 'checkout-redirect' })

            const response = await subscriptionApi.createCheckoutSession({
                plan_type,
                price_id,
            })

            if (response.success && response.data.checkout_url) {
                toast.dismiss('checkout-redirect')
                writePendingPlanType(plan_type ?? null)
                setCheckoutUrl(response.data.checkout_url)
                setIsCheckoutOpen(true)
            } else {
                const errorMsg = typeof response.message === 'object'
                    ? (response.message as any).description || (response.message as any).title
                    : response.message;
                toast.error(errorMsg || 'Failed to create checkout session', { id: 'checkout-redirect' })
            }
        } catch (error: any) {
            console.error('Upgrade error:', error)
            const errorData = error.response?.data?.message;
            const errorMsg = typeof errorData === 'object'
                ? errorData.description || errorData.title
                : errorData;
            toast.error(errorMsg || 'Failed to initiate upgrade', { id: 'checkout-redirect' })
        }
    }, [subscription?.plan_type])

    const cancelSubscription = useCallback(async () => {
        try {
            toast.loading('Canceling subscription...', { id: 'cancel-subscription' })
            const response = await subscriptionApi.cancelSubscription()

            if (response.success) {
                const successMsg = typeof response.message === 'object'
                    ? (response.message as any).description || (response.message as any).title
                    : response.message;
                toast.success(successMsg || 'Auto-pay cancelled. Access continues until period end.', { id: 'cancel-subscription' })
                if (response.data) {
                    setSubscription(response.data)
                } else {
                    await fetchSubscription()
                }
            } else {
                const errorMsg = typeof response.message === 'object'
                    ? (response.message as any).description || (response.message as any).title
                    : response.message;
                toast.error(errorMsg || 'Failed to cancel subscription', { id: 'cancel-subscription' })
            }
        } catch (error: any) {
            console.error('Cancel subscription error:', error)
            const errorData = error.response?.data?.message;
            const errorMsg = typeof errorData === 'object'
                ? errorData.description || errorData.title
                : errorData;
            toast.error(errorMsg || 'Failed to cancel subscription', { id: 'cancel-subscription' })
        }
    }, [fetchSubscription])

    const isProfessionalStatus = useCallback((data: SubscriptionData | null | undefined) =>
        data?.is_professional ??
        (data?.plan_name === 'PROFESSIONAL' && data?.status === 'active'),
    [])

    const matchesExpectedPlan = useCallback((data: SubscriptionData | null | undefined) => {
        if (!isProfessionalStatus(data)) return false
        const expected = readPendingPlanType()
        // No expected plan — any Professional activation is enough.
        if (!expected) return true
        return data?.plan_type === expected
    }, [isProfessionalStatus])

    const markActivated = useCallback((data: SubscriptionData) => {
        writePendingPlanType(null)
        setSubscription(data)
        const successLabel =
            data.plan_type === 'YEARLY'
                ? 'Welcome to Professional Yearly!'
                : 'Welcome to Professional!'
        toast.success(successLabel, { id: 'activate-subscription' })
        return true
    }, [])

    /**
     * After HPP payment: poll local DB for webhook, then auto-pull from gateway
     * (same path as manual Sync) so the user does not have to sync again.
     */
    const confirmPaymentActivation = useCallback(async (): Promise<boolean> => {
        const toastId = 'activate-subscription'
        const webhookAttempts = 6
        const syncAttempts = 4
        const intervalMs = 1500
        const expected = readPendingPlanType()
        const loadingLabel =
            expected === 'YEARLY'
                ? 'Activating your Yearly plan...'
                : 'Activating your Professional plan...'

        toast.loading(loadingLabel, { id: toastId })

        // Phase 1 — webhook may already have updated /current
        for (let attempt = 0; attempt < webhookAttempts; attempt++) {
            try {
                const result = await subscriptionApi.getCurrentSubscription()
                if (result.success && result.data && matchesExpectedPlan(result.data)) {
                    return markActivated(result.data)
                }
                if (result.success && result.data) {
                    setSubscription(result.data)
                }
            } catch (error) {
                console.warn('Error polling subscription after payment:', error)
            }

            if (attempt < webhookAttempts - 1) {
                await new Promise((resolve) => setTimeout(resolve, intervalMs))
            }
        }

        // Phase 2 — webhook delayed: same gateway pull as the Sync button
        toast.loading('Finalizing your plan with the payment provider...', {
            id: toastId,
        })

        for (let attempt = 0; attempt < syncAttempts; attempt++) {
            try {
                const response = await subscriptionApi.syncPending()
                if (response.success && response.data) {
                    setSubscription(response.data)
                    if (matchesExpectedPlan(response.data)) {
                        return markActivated(response.data)
                    }
                    // Professional is live but billing cycle still catching up
                    if (isProfessionalStatus(response.data)) {
                        writePendingPlanType(null)
                        toast.success('Welcome to Professional!', { id: toastId })
                        return true
                    }
                }
            } catch (error) {
                console.warn('Error syncing subscription after payment:', error)
            }

            if (attempt < syncAttempts - 1) {
                await new Promise((resolve) => setTimeout(resolve, intervalMs))
            }
        }

        toast.info(
            'Payment received. Your plan will update in a moment — use Sync if it does not appear.',
            { id: toastId }
        )
        return false
    }, [matchesExpectedPlan, markActivated, isProfessionalStatus])

    const syncSubscription = useCallback(async () => {
        try {
            toast.loading('Syncing subscription...', { id: 'sync-subscription' })
            const response = await subscriptionApi.syncPending()

            if (response.success) {
                const syncedProfessional = isProfessionalStatus(response.data)
                const planReady = matchesExpectedPlan(response.data)
                const successMsg = typeof response.message === 'object'
                    ? (response.message as any).description || (response.message as any).title
                    : response.message;
                if (planReady) {
                    writePendingPlanType(null)
                    toast.success(successMsg || 'Subscription synced successfully!', { id: 'sync-subscription' })
                } else if (syncedProfessional) {
                    toast.info(
                        'Your Professional plan is active, but the upgraded billing cycle may still be updating. Try Sync again in a moment.',
                        { id: 'sync-subscription' }
                    )
                } else {
                    toast.info(
                        'No active paid subscription found for your account email yet. Complete payment on MyPaymentNow using the same email as your account, then sync again.',
                        { id: 'sync-subscription' }
                    )
                }
                if (response.data) {
                    setSubscription(response.data)
                } else {
                    await fetchSubscription()
                }
            } else {
                const errorMsg = typeof response.message === 'object'
                    ? (response.message as any).description || (response.message as any).title
                    : response.message;
                toast.error(errorMsg || 'Failed to sync subscription', { id: 'sync-subscription' })
            }
        } catch (error: any) {
            console.error('Sync subscription error:', error)
            const errorData = error.response?.data?.message;
            const errorMsg = typeof errorData === 'object'
                ? errorData.description || errorData.title
                : errorData;
            toast.error(errorMsg || 'Failed to sync subscription', { id: 'sync-subscription' })
        }
    }, [fetchSubscription, isProfessionalStatus, matchesExpectedPlan])

    const isProfessional = isProfessionalStatus(subscription)
    const isStarter = subscription?.plan_name === 'STARTER'

    const value = React.useMemo<SubscriptionContextType>(
        () => ({
            subscription,
            isLoading,
            isProfessional,
            isStarter,
            refreshSubscription,
            handleUpgrade,
            cancelSubscription,
            syncSubscription,
            confirmPaymentActivation,
        }),
        [
            subscription,
            isLoading,
            isProfessional,
            isStarter,
            refreshSubscription,
            handleUpgrade,
            cancelSubscription,
            syncSubscription,
            confirmPaymentActivation,
        ]
    )

    return (
        <SubscriptionContext.Provider value={value}>
            <SubscriptionLimitModal />
            <HppCheckoutModal
                open={isCheckoutOpen}
                checkoutUrl={checkoutUrl}
                onOpenChange={(open) => {
                    setIsCheckoutOpen(open)
                    if (!open) setCheckoutUrl(null)
                }}
                onPaymentComplete={async () => {
                    setIsCheckoutOpen(false)
                    setCheckoutUrl(null)
                    const activated = await confirmPaymentActivation()
                    router.push(activated ? '/subscription' : '/subscription?payment=pending')
                }}
            />
            {children}
        </SubscriptionContext.Provider>
    )
}

export function useSubscription() {
    const context = useContext(SubscriptionContext)
    if (context === undefined) {
        throw new Error('useSubscription must be used within a SubscriptionProvider')
    }
    return context
}
